import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "./api";

const AuthContext = createContext(null);
const CHILD_LODGE_KEY = "sc_active_child_lodge";
const CHILD_LODGE_NAME_KEY = "sc_active_child_lodge_name";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown; false = unauth; obj = authenticated
  const [loading, setLoading] = useState(true);
  const [activeChildLodge, setActiveChildLodgeState] = useState(() => {
    if (typeof window === "undefined") return null;
    const id = localStorage.getItem(CHILD_LODGE_KEY);
    const name = localStorage.getItem(CHILD_LODGE_NAME_KEY);
    return id ? { id, name: name || "" } : null;
  });

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (username, password) => {
    try {
      const { data } = await api.post("/auth/login", { username, password });
      // Step-up flows: backend returns mfa_required / password_change_required
      // INSTEAD of access_token. We don't set the user in this case — the
      // SPA shows the MFA / password-change UI and proceeds in a follow-up
      // call.
      if (data?.mfa_required) {
        return { ok: true, mfaRequired: true, mfaToken: data.mfa_token, username: data.username };
      }
      if (data?.password_change_required) {
        return {
          ok: true,
          passwordChangeRequired: true,
          mfaToken: data.mfa_token,
          username: data.username,
        };
      }
      if (data?.access_token) localStorage.setItem("sc_token", data.access_token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const completeMfa = async (mfaToken, code, useBackup = false) => {
    try {
      const { data } = await api.post("/auth/login/mfa", {
        mfa_token: mfaToken,
        code,
        backup_code: useBackup,
      });
      if (data?.access_token) localStorage.setItem("sc_token", data.access_token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      // ignore
    }
    localStorage.removeItem("sc_token");
    localStorage.removeItem(CHILD_LODGE_KEY);
    localStorage.removeItem(CHILD_LODGE_NAME_KEY);
    setActiveChildLodgeState(null);
    setUser(false);
  };

  const enterChildLodge = useCallback((lodge) => {
    if (!lodge?.id) return;
    localStorage.setItem(CHILD_LODGE_KEY, lodge.id);
    localStorage.setItem(CHILD_LODGE_NAME_KEY, lodge.name || "");
    setActiveChildLodgeState({ id: lodge.id, name: lodge.name || "" });
  }, []);

  const exitChildLodge = useCallback(() => {
    localStorage.removeItem(CHILD_LODGE_KEY);
    localStorage.removeItem(CHILD_LODGE_NAME_KEY);
    setActiveChildLodgeState(null);
  }, []);

  // Effective view for downstream consumers: an obedience admin entering a
  // child lodge gets `child_lodge_id` injected so menus / scoping logic
  // mirror the backend's impersonation rules.
  const effectiveUser = user && typeof user === "object" && activeChildLodge && !user.child_lodge_id
    ? { ...user, child_lodge_id: activeChildLodge.id, _impersonating: true, _impersonating_name: activeChildLodge.name }
    : user;

  return (
    <AuthContext.Provider
      value={{
        user: effectiveUser,
        rawUser: user,
        loading,
        login,
        completeMfa,
        logout,
        refreshUser: fetchMe,
        activeChildLodge,
        enterChildLodge,
        exitChildLodge,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
